<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useElementSize } from '@vueuse/core';
import { AppCoreEnums } from '../shared/enums';
import { useRoute, useRouter } from 'vue-router';
import tabpreset from '@/presets/customizations/tab';
import { ITabsContainerTab } from '../models/routing';
import tabspreset from '@/presets/customizations/tabs';
import { useCoreStore } from '../../stores/core/store';
import { computed, PropType, ref, watchEffect } from 'vue';
import AppButton from '../components/common/app-button.vue';
import tablistpreset from '@/presets/customizations/tablist';
import useRouteUtils from '../../core/router-utils/route-utils';
import useFormsRouteUtils from '../router-utils/forms-route-utils';

const props = defineProps({
  items: {
    type: Array as PropType<ITabsContainerTab[]>,
    required: true,
  },
});

const titleRef = ref();
const activeTab = ref();
const route = useRoute();
const router = useRouter();
const componentKey = ref(0);
const tabsContainerRef = ref();
const coreStore = useCoreStore();
const { saveFormAsync } = useFormsRouteUtils();
const { getContainerRouteName } = useRouteUtils();
const { height } = useElementSize(tabsContainerRef);
const { height: titleHeight } = useElementSize(titleRef);
const { collapsibleTableContainerData, isCollapsibleTableContainerMinimized, sizes } = storeToRefs(coreStore);

const isRouteActive = (routeName: string) => {
  if (!routeName) return false;

  // Check if current route matches
  if (route.name === routeName) return true;

  // Check if any child route matches
  const matchedRoutes = route.matched;
  for (const matchedRoute of matchedRoutes) {
    if (matchedRoute.name === routeName) return true;
  }

  return false;
};

const activeTabValue = computed(() => {
  const activeItem = props.items.find((item: ITabsContainerTab) => isRouteActive(item.to));
  return activeItem?.to || '';
});

const onClose = () => {
  const parentRouteName = getContainerRouteName(route, AppCoreEnums.ContainerType.CollapsibleTableContainer);
  router.push({ name: parentRouteName });
};

const title = computed(() => collapsibleTableContainerData.value.detailsTitle);

const onNavigate = (navigate: Function, event: MouseEvent) => {
  const origEvent = { ...event };
  event.preventDefault();
  event.stopPropagation();

  saveFormAsync().then((wasSuccessfully) => {
    if (wasSuccessfully) navigate(origEvent);
  });
};

watchEffect(() => (activeTab.value = route.name));
watchEffect(() => (sizes.value.tabsContainerHeight = height.value));
watchEffect(() => (sizes.value.tabsContainerTitleHeight = titleHeight.value));
</script>

<template>
  <div class="flex flex-col" v-if="!isCollapsibleTableContainerMinimized" :key="componentKey" ref="tabsContainerRef">
    <div class="flex justify-between w-full text-gray-900 dark:text-gray-100 font-semibold text-[30px] mb-5 pl-[2px]">
      <div ref="titleRef">{{ title }}</div>
      <!--------------------------------------------------------
      -- Close Collapsible Table Container Right Panel Button --
      ---------------------------------------------------------->
      <app-button severity="regular" outlined icon="hugeicons:cancel-01" @click="onClose" class="mr-1" tooltip="core.close" />
    </div>

    <Tabs :value="activeTabValue" :ptOptions="{ mergeSections: true, mergeProps: true }" :pt="tabspreset">
      <TabList :ptOptions="{ mergeSections: true, mergeProps: true }" :pt="tablistpreset">
        <Tab
          v-for="item in items"
          :key="item.label"
          :value="item.to"
          :ptOptions="{ mergeSections: true, mergeProps: true }"
          :pt="tabpreset"
        >
          <router-link v-slot="{ href, navigate }" :to="{ name: item.to }" custom>
            <a
              v-ripple
              :href="href"
              @click="onNavigate(navigate, $event)"
              :class="[
                'flex items-center gap-2 text-inherit h-[43px] px-3',
                { 'dark:bg-slate-800 dark:text-slate-300': isRouteActive(item.to) },
              ]"
            >
              <Icon :icon="item.icon" class="w-[18px] h-[18px]" v-if="item.icon" />
              <span class="font-bold">{{ $t(item.label) }}</span>
              <Badge v-if="item.badgeValue" :value="item.badgeValue().value" class="custom-badge w-[24px] h-[24px]" />
            </a>
          </router-link>
        </Tab>
      </TabList>
    </Tabs>
  </div>

  <!-----------
  -- Content --
  ------------->
  <div class="mt-5 flex flex-col align-items-center justify-content-center">
    <router-view></router-view>
  </div>
</template>
<style>
.custom-badge {
  background-color: #fef3f2;
  color: #b42318;
  border: 1px solid #fecdca;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  text-align: center;
  padding: 0;
}
</style>
